import { getOrderDetails } from '@app/ducks';
import { INFINITE_SELECTIONS_QUERY_KEY } from '@app/services/hooks/orders/useInfiniteSelectionsList';
import { InfiniteData, useQueryClient } from '@tanstack/react-query';
import { Order } from '@typings';
import React from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { isDefined } from '../is';

export const useAvailableOrderDetails = () => {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const orderDetails = useSelector(getOrderDetails) as Partial<Order.Single>;

  const selections =
    queryClient
      .getQueriesData<InfiniteData<Responses.Orders>>({
        queryKey: [INFINITE_SELECTIONS_QUERY_KEY],
      })[0]?.[1]
      ?.pages.flatMap(page => page.orders) ?? [];

  const fallbackOrder = selections.find(({ order }) => order === id);

  const { order, buyer, account } = orderDetails;

  const orderData = isDefined(order?.order) ? order : fallbackOrder;

  return React.useMemo(
    () => ({
      accountId: account?.account ?? fallbackOrder?.account,
      accountName: account?.name ?? fallbackOrder?.accountName ?? '',
      buyerFirstName: buyer?.firstName ?? fallbackOrder?.buyerFirstName ?? '',
      buyerId: buyer?.buyer ?? fallbackOrder?.buyer,
      buyerLastName: buyer?.lastName ?? fallbackOrder?.buyerLastName ?? '',
      deliveryWindowsIds: orderData?.deliveryWindows,
      hasAnyOrderData: isDefined(orderData),
      orderDate: orderData?.orderDate,
      orderId: orderData?.order ?? '',
      orderName: orderData?.name ?? '',
      orderNumber: orderData?.orderNumber ?? '',
      orderStatus: orderData?.status,
      orderTotals: orderData?.totals,
      poNumber: orderData?.poNumber,
    }),
    [account, buyer, fallbackOrder, orderData],
  );
};
